import { useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useMaterialsUploadQueue,
  type MaterialsUploadJob,
} from "@/lib/materials-upload-queue";
import { cn } from "@/lib/utils";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function jobProgressRatio(job: MaterialsUploadJob): number {
  if (job.status === "done") return 1;
  if (job.status === "error" || job.status === "queued") return 0;
  return job.progress;
}

export function MaterialsUploadProgress() {
  const { jobs, activeCount, dismissIdle } = useMaterialsUploadQueue();
  const [expanded, setExpanded] = useState(true);
  const prevJobCountRef = useRef(0);

  // 新一批任务出现时默认展开，方便看到刚加入的队列
  useEffect(() => {
    if (jobs.length > 0 && prevJobCountRef.current === 0) {
      setExpanded(true);
    }
    prevJobCountRef.current = jobs.length;
  }, [jobs.length]);

  if (jobs.length === 0) return null;

  const total = jobs.length;
  const finished = jobs.filter((j) => j.status === "done" || j.status === "error").length;
  const overall =
    total === 0
      ? 0
      : jobs.reduce((sum, job) => sum + jobProgressRatio(job), 0) / total;
  const busy = activeCount > 0;
  const title = busy ? "正在上传" : "上传完成";
  const countLabel = `${finished}/${total}`;

  return (
    <div
      className="overflow-hidden rounded-xl border border-border/80 bg-background/95 shadow-lg backdrop-blur supports-backdrop-filter:bg-background/90"
      role="status"
      aria-live="polite"
      aria-label="素材上传进度"
    >
        {expanded ? (
          <div className="p-3">
            <div className="flex items-start gap-2">
              <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Upload className="size-4" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">{title}</p>
                  <div className="flex items-center gap-0.5">
                    <span className="px-1 text-xs text-muted-foreground tabular-nums">
                      {countLabel}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className="size-6"
                      aria-label="收起上传面板"
                      aria-expanded={true}
                      onClick={() => setExpanded(false)}
                    >
                      <ChevronUp className="size-3.5" />
                    </Button>
                    {!busy ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className="size-6"
                        aria-label="关闭上传面板"
                        onClick={dismissIdle}
                      >
                        <X className="size-3.5" />
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-[width] duration-200 ease-out"
                    style={{ width: `${Math.round(overall * 100)}%` }}
                  />
                </div>
              </div>
            </div>

            <ul className="mt-3 max-h-40 space-y-2 overflow-y-auto overscroll-contain">
              {jobs.map((job) => (
                <li key={job.id} className="flex items-start gap-2 text-xs">
                  <StatusIcon status={job.status} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium" title={job.fileName}>
                      {job.fileName}
                    </p>
                    <p className="text-muted-foreground">
                      {job.status === "error"
                        ? job.error || "失败"
                        : job.status === "done"
                          ? formatBytes(job.byteSize)
                          : job.status === "uploading"
                            ? `${Math.round(job.progress * 100)}% · ${formatBytes(job.byteSize)}`
                            : `等待中 · ${formatBytes(job.byteSize)}`}
                    </p>
                    {job.status === "uploading" ? (
                      <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary/80 transition-[width] duration-150"
                          style={{ width: `${Math.round(job.progress * 100)}%` }}
                        />
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <button
            type="button"
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-muted/40"
            aria-label={`${title} ${countLabel}，点击展开`}
            aria-expanded={false}
            onClick={() => setExpanded(true)}
          >
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              {busy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Upload className="size-4" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-medium">{title}</p>
                <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground tabular-nums">
                  {countLabel}
                  <ChevronDown className="size-3.5 opacity-70" />
                </span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-200 ease-out"
                  style={{ width: `${Math.round(overall * 100)}%` }}
                />
              </div>
            </div>
          </button>
        )}
    </div>
  );
}

function StatusIcon({ status }: { status: MaterialsUploadJob["status"] }) {
  if (status === "done") {
    return <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-emerald-600" />;
  }
  if (status === "error") {
    return <XCircle className="mt-0.5 size-3.5 shrink-0 text-destructive" />;
  }
  if (status === "uploading") {
    return <Loader2 className="mt-0.5 size-3.5 shrink-0 animate-spin text-primary" />;
  }
  return (
    <span
      className={cn("mt-1 size-3.5 shrink-0 rounded-full border border-muted-foreground/40")}
      aria-hidden
    />
  );
}
